import * as _ from "lodash";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider } from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_DataMartPlanRelationshipRow,
    WB_DataMartRow,
    WB_MasterDataSetRow, WB_PlanRow,
    WB_SalesOrgRow
} from "../PA.Core/PAdbSchemaRows";
import { IwbDataMart } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_DataMart,
    esWB_DataMart_Plan_Relationship,
    esWB_MasterData_Set,   esWB_Plan,
    esWB_SalesOrg
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"

import {PAEnvironment} from "../PA.Core/PAEnvironment";
import { DataMartBOValidations } from "./DataMartBO.Validation";
import { MDUsageService } from "../PA.MD.Services/MDUsageService";


export class DataMartBO
    implements IBusinessObject
{

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_DataMart: WB_DataMartRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New DataMart'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_DataMart = null;

    }

    get WB_DataMart(): WB_DataMartRow {
        return this.drWB_DataMart;
    }

    //</editor-fold>

    //<editor-fold desc="IBusinessObject" operations>

    async LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating DataMart");
        let internalOnComplete = self._OnLoadComplete = (async () => {
            try {
                self.LoadOptions = options || {};
                if ("dbContext" in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (this.dbContext == null) {
                    let sError = "Unable to determine dbContext for new DataMartBO";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                //Create record using dbContext
                let newDataMartRow: WB_DataMartRow = <WB_DataMartRow>await self.dbContext.createRecordWithKey(esWB_DataMart)

                newDataMartRow.setDefaults();
                self.drWB_DataMart = newDataMartRow;
                //add this to all the BOs
                self.dbContext.add(esWB_DataMart, newDataMartRow);
                self.Identity.setAll(self.WB_DataMart.cif.WB_DATAMART_ID, self.WB_DataMart.cif.DM_ID, self.WB_DataMart.cif.DM_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_DataMart, "WB_DATAMART_ID", "DM_ID", "DM_NAME");

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                await self.applyCreateOptions()
                self.Identity.setAll(self.WB_DataMart.cif.WB_DATAMART_ID, self.WB_DataMart.cif.DM_ID, self.WB_DataMart.cif.DM_NAME);

                return self;
            } catch (err) {

                self.LoadProgressProvider.exception(err)
                return self
            }
        })()

        return internalOnComplete
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId))
            self.WB_DataMart.cif.DM_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_DataMart.cif.DM_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_DataMart.WB_MDSetRow = mds
                self.WB_DataMart.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Sales Org
        let wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId
        if (_.isNumber(wbSalesOrg)) {
            let salesOrg: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, wbSalesOrg)
            if (salesOrg) {
                self.WB_DataMart.WB_SalesOrgRow = salesOrg
                self.WB_DataMart.cif.WB_SALESORG_ID = wbSalesOrg
            }
        }

        // Set Entities
        const wbEntityIdCustomer = self.CreateOptions?.wbEntityIdCustomer || self.LoadOptions?.wbEntityIdCustomer;
        if (_.isNumber(wbEntityIdCustomer)) {
            self.WB_DataMart.cif.WB_ENTITY_ID_CUSTOMER = wbEntityIdCustomer;
        }

        const wbEntityIdPrd = self.CreateOptions?.wbEntityIdProduct || self.LoadOptions?.wbEntityIdProduct;
        if (_.isNumber(wbEntityIdPrd)) {
            self.WB_DataMart.cif.WB_ENTITY_ID_PRODUCT = wbEntityIdPrd;
        }

        const wbEntityIdPeriod = self.CreateOptions?.wbEntityIdPeriod || self.LoadOptions?.wbEntityIdPeriod;
        if (_.isNumber(wbEntityIdPeriod)) {
            self.WB_DataMart.cif.WB_ENTITY_ID_PERIOD = wbEntityIdPeriod;
        }

        self.WB_DataMart.cif.PLAN_SCOPE_MANUAL_IND = '0'
    }

    async Load(anId: number | number, options?: any): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete =  (async () => {
            try {

            if (self.bIsLoaded) {
                let sError = "DataMart already loaded";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                return self;
            }

            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for new DataMart";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                return self;
            }

            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = DataMartBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = DataMartBO.setupRecordResolver(recordResolver, [anId]);
            }

            await recordResolverLoadPromise
                    let aDataMartRow: any = recordResolver.get(esWB_DataMart, anId);

                    if (_.isEmpty(aDataMartRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        return self;
                    }

                    self.drWB_DataMart = aDataMartRow;
                    self.Identity.setAll(self.WB_DataMart.cif.WB_DATAMART_ID, self.WB_DataMart.cif.DM_ID, self.WB_DataMart.cif.DM_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_DataMart, "WB_DATAMART_ID", "DM_ID", "DM_NAME");
                    if (_.isNumber(self.drWB_DataMart.cif.WB_DATAMART_ID) && self.drWB_DataMart.cif.WB_DATAMART_ID > 0) {
                        let mruService = new MDUsageService()
                        await mruService.saveDataMartUsage(self.drWB_DataMart.cif.WB_DATAMART_ID)
                    }
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    return self;
                }
                catch(err)  {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    return self;
                }
        })();
        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_DataMart.cif.DM_NAME + "(" + self.WB_DataMart.cif.DM_ID + ")");

        // Does no validations yet
        self.ValidateOptions = options || {};

        try {
            if (self.dbContext == undefined)
                throw "Unable to determine dbContext for to Validate DataMart.";

            await DataMartBOValidations.ValidateFields(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0){
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();


        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
        }
        return self;
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbDataMart = <IwbDataMart>self.drWB_DataMart;
        this.SaveProgressProvider.start("Saving " + wbDataMart.DM_NAME + "(" + wbDataMart.DM_ID + ")");

        try {
            if (self.dbContext == null) {
                self._bIsSaved = false;
                const sError: string = "Unable to determine dbContext to Save DataMart.";
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            await self.Validate({});
            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            wbDataMart.DM_TYPE = wbDataMart.PLAN_SCOPE_MANUAL_IND === "1" ? "Manual" : "By Year";

            const tasks: Promise<any>[] = [];
            tasks.push(saveFunction(esWB_DataMart, self.WB_DataMart, {}));
            tasks.push(saveFunction(esWB_DataMart_Plan_Relationship, self.WB_DataMart.WB_DataMartPlanRelationshipRows, {}));
            await Promise.all(tasks);

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }

        return self;
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbDataMart = <IwbDataMart>self.drWB_DataMart;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbDataMart.DM_NAME + "(" + wbDataMart.DM_ID + ")");

        try {
            // 1. Validate if we can delete the DataMart Source

            // 2. If Valid, mark all the records for deletion.
            if (self.DeleteMessageProvider.countMessages(MessageType.Error) > 0){
                // Don't delete..
                this.DeleteProgressProvider.finish();
                return self;
            }

            self.drWB_DataMart.markDeleted();

            _.forEach(self.drWB_DataMart.WB_DataMartPlanRelationshipRows, (row) => {
                if (row.isActive) {
                    row.markDeleted();
                }
            });

            if (bSaveNow) {
                await self.SaveUsing(false);
            }

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            this.DeleteProgressProvider.exception(err);
        }

        return self;
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async addPlanR(plan: number): Promise<WB_DataMartPlanRelationshipRow> {
        const self = this

        let found = false
        _.forEach(self.WB_DataMart.WB_DataMartPlanRelationshipRows, aPlan=>{
            if(aPlan.cif.WB_PLAN_ID == plan && aPlan.isActive)
                found = true
        })
        if(found)
            return
        let result: WB_PlanRow = await self.dbContext.find(esWB_Plan, plan)
        if (_.isEmpty(result))
            return

        let aNewRow: WB_DataMartPlanRelationshipRow = <any>await self.dbContext.createRecordWithKey(esWB_DataMart_Plan_Relationship)
        aNewRow.setDefaults()
        aNewRow.cif.WB_DATAMART_ID = self.drWB_DataMart.cif.WB_DATAMART_ID
        aNewRow.cif.WB_PLAN_ID = plan
        aNewRow.WB_PlanRow = result
        aNewRow.WB_DataMartRow = self.drWB_DataMart
        aNewRow.cif.MANUAL_IND = '1'
        self.dbContext.add(esWB_DataMart_Plan_Relationship, aNewRow)

        return aNewRow
    }

    async removePlanR(planR: number): Promise<void> {
        const self = this

        _.forEach(self.WB_DataMart.WB_DataMartPlanRelationshipRows, aPlanR=>{
            if(aPlanR.cif.WB_DATAMARTPLNR_ID == planR && aPlanR.isActive)
                aPlanR.markDeleted()
        })

    }

    //</editor-fold>

    //<editor-fold desc="IRecordResolver" operations>

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const dataMart_options: CreateOptions = {
            standardConditions: {
                [esWB_DataMart_Plan_Relationship]: { DELETED_IND: { neq: "1" } },
            },
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, dataMart_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_DataMart, ids)
                .then((result) => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
        })
    }


    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( DataMartBO, "DataMartBO", "DataMart")